/**
 * useLiveMatch — subscribe to real-time ball events for a match.
 *
 * Auth: cookie-session only. The browser sends the session cookie
 * automatically on same-origin (`api.zencricketdata.com`) WS upgrades.
 * Bearer tokens are NOT supported here — placing them in the URL
 * (`?token=...`) leaks them into proxy logs and browser history.
 * If a token-auth WS becomes a requirement, add a backend handshake
 * that accepts a subprotocol header instead.
 */
import { useEffect, useRef, useState } from "react";

const WS_BASE = (typeof window !== "undefined" && window.location.host.includes("zencricketdata.com"))
  ? "wss://api.zencricketdata.com"
  : "ws://localhost:8001";  // local dev fallback


/**
 * Args:
 *  - matchId:        legacy per-tenant match — uses /ws/v1/match/<id>/
 *  - canonicalId:    canonical match — uses /ws/v1/canonical/<id>/
 *  - feed:           "provisional" | "confirmed" | "certified" (canonical only)
 *  - autoConnect, maxEvents
 */
export function useLiveMatch({
  matchId,
  canonicalId,
  feed = "confirmed",
  autoConnect = true,
  maxEvents = 50,
}) {
  const [connected, setConnected] = useState(false);
  const [error, setError]         = useState("");
  const [events, setEvents]       = useState([]);
  const [latest, setLatest]       = useState(null);
  const [license, setLicense]     = useState(null);
  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  useEffect(() => {
    if (!autoConnect) return;
    if (!matchId && !canonicalId) return;
    let alive = true;

    const connect = () => {
      const params = new URLSearchParams();
      if (canonicalId && feed) params.set("feed", feed);
      const path = canonicalId
        ? `/ws/v1/canonical/${canonicalId}/`
        : `/ws/v1/match/${matchId}/`;
      const qs = params.toString();
      const url = `${WS_BASE}${path}${qs ? "?" + qs : ""}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!alive) return;
        setConnected(true);
        setError("");
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (e) => {
        if (!alive) return;
        try {
          const msg = JSON.parse(e.data);
          if (
            msg.type === "ball.event"
            || msg.type === "ball"
            || msg.type === "ball_clip_ready"
            || msg.type === "ball_clip_failed"
          ) {
            // Clip events ride the same WS as ball events so subscribers
            // (e.g. BallVideoPlayer) can react via the existing `latest`
            // channel instead of opening a second socket.
            setLatest(msg);
            setEvents((prev) => [...prev.slice(-(maxEvents - 1)), msg]);
          } else if (msg.type === "subscribed") {
            if (msg.license) setLicense(msg.license);
          }
        } catch (err) {
          console.warn("Bad WS message:", e.data);
        }
      };

      ws.onerror = (e) => {
        if (!alive) return;
        setError("WebSocket error");
      };

      ws.onclose = (e) => {
        if (!alive) return;
        setConnected(false);
        // 4003 = unauthorized scope; don't retry
        if (e.code === 4003) {
          setError(`Token missing scope for feed: ${feed}`);
          return;
        }
        // Exponential backoff reconnect
        const attempts = ++reconnectAttemptsRef.current;
        const delay = Math.min(30000, 1000 * Math.pow(2, attempts));
        setTimeout(() => {
          if (alive) connect();
        }, delay);
      };
    };

    connect();

    return () => {
      alive = false;
      try { wsRef.current?.close(); } catch {}
    };
    // `token` is intentionally not in this dependency list — auth is
    // cookie-session only (see header docstring); the previous
    // reference to a `token` variable was a leftover from an earlier
    // bearer-WS prototype and threw ReferenceError at render time,
    // which surfaced as "token is not defined" on the Scorecard page.
  }, [matchId, canonicalId, feed, autoConnect, maxEvents]);

  return { connected, error, events, latest, license };
}
